/**
 * Punkty na modelach, tak jak opisuje je serwer.
 *
 * **Model przyjeżdża z eksportu jako sama geometria** i nie niesie w sobie
 * niczego, co mówiłoby „komin jest tutaj". Dopóki nie było gdzie tego zapisać,
 * ogień i dym siadały w **środku pola** — a komin narysowany z boku dachu
 * świecił ze środka podłogi. Punkty ustawia się w panelu administratora,
 * a jadą tą samą drogą, co katalog budynków i legenda biomów: jeden plik na
 * serwerze, pobierany raz przy wczytywaniu modeli.
 *
 * **Współrzędne są w przestrzeni znormalizowanego modelu** — tej, którą
 * produkuje `flatten()` z `PropModels`: podstawa na zerze, wyśrodkowanie na
 * własnym rzucie, wysokość równa jedynce. Dzięki temu punkt nie zależy od
 * skali, w jakiej plik wyszedł z eksportu, i przeżywa jego podmianę, o ile
 * bryła zostanie podobna. Do jednostek świata przelicza je `adoptModels`, tą
 * samą skalą, którą wypieka w geometrię — bo to jest ta sama zamiana.
 *
 * Klucz to **nazwa pliku**, nie rodzaj budynku: wioska to obóz na drugim
 * szczeblu, czyli inny plik i inny dach, a punkt należy do bryły, którą widać.
 */

/** Punkt w przestrzeni znormalizowanego modelu. */
export type Anchor = [number, number, number];

/** Co ustawiono na jednej bryle. */
export interface ModelAnchors {
  /** Skąd świeci ogień. Klient zapala **pierwszy** — patrz `Hearths`. */
  hearth?: Anchor[];
  /** Skąd idzie dym. Wszystkie naraz: dym jest jednym draw callem. */
  smoke?: Anchor[];
  /** Gdzie stoi podstawa drzewca sztandaru — pierwszy się liczy. */
  banner?: Anchor[];
  /** Na modelu drzewca: górny i dolny punkt przybicia płata. */
  attach?: Anchor[];
}

/** Punkty wszystkich modeli, po nazwie pliku. */
export type Anchors = Record<string, ModelAnchors>;

/**
 * Pobierz to, co ustawiono.
 *
 * **Niepowodzenie nie zatrzymuje wejścia do świata**, dokładnie jak brakujący
 * plik modelu: bez punktów ogień i dym wracają tam, gdzie siadały zawsze —
 * na środek pola. Świat bez komina na właściwym miejscu jest grywalny, świat,
 * który się nie otworzył, nie jest.
 */
export async function loadAnchors(api: string): Promise<Anchors> {
  try {
    const response = await fetch(`${api}/api/world/models`);
    if (!response.ok) throw new Error(`${response.status}`);
    return (await response.json()) as Anchors;
  } catch (error) {
    console.warn('punkty modeli nie wczytały się:', error);
    return {};
  }
}

/**
 * Jedna bryła w kompozycji — w **przestrzeni heksa**: środek pola w zerze,
 * promień (środek → narożnik) równy jedynce, front kompozycji w stronę +Z.
 * Kształt opisuje serwer (`assets.rs`).
 */
export interface CompositionPart {
  /** Plik z `public/models`, bez rozszerzenia — także `parts/…`. */
  shape: string;
  at: Anchor;
  /** Obrót wokół pionu, w radianach. */
  turn?: number;
  scale?: number;
}

/** Budynek złożony z brył, plus punkty samej kompozycji. */
export interface Composition {
  parts: CompositionPart[];
  anchors?: ModelAnchors;
}

/** Kompozycje po nazwie — tej, którą wskazuje `model` w katalogu. */
export type Compositions = Record<string, Composition>;

/**
 * Pobierz kompozycje. Niepowodzenie znaczy „żadnych" — nazwy z katalogu są
 * wtedy czytane jak pliki, czyli tak, jak przed kompozycjami.
 */
export async function loadCompositions(api: string): Promise<Compositions> {
  try {
    const response = await fetch(`${api}/api/world/compositions`);
    if (!response.ok) throw new Error(`${response.status}`);
    return (await response.json()) as Compositions;
  } catch (error) {
    console.warn('kompozycje nie wczytały się:', error);
    return {};
  }
}
